import { useEffect, useState, useSyncExternalStore } from 'react'

function useIsMobile(breakpoint = 768) {
  return useSyncExternalStore(
    cb => { window.addEventListener('resize', cb); return () => window.removeEventListener('resize', cb) },
    () => window.innerWidth < breakpoint,
    () => false,
  )
}
import Select from 'react-select'
import { Select as ThemedSelect } from '../components/ui'
import { apiKeysApiService } from '../services/apiKeysApiService'
import { renderApiService, type RenderTrace } from '../services/renderApiService'
import { config } from '../config/environment'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'
import type { ApiKey } from '../types'

const BASE_URL = `${config.apiBaseUrl}/v1`
const FORMATS   = ['pdf', 'docx', 'xlsx', 'pptx', 'html', 'txt', 'csv', 'odt']
const ON_MISSING = ['blank', 'error', 'hide']
const EX_VERSION = 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
const EX_JOB     = EX_VERSION

const EXAMPLE_PAYLOAD = `{
  "customerName": "Acme Corp",
  "invoiceNumber": "INV-2024-001",
  "items": [
    { "description": "Consulting services", "amount": 1500 },
    { "description": "Software license",    "amount": 499  }
  ],
  "total": 1999,
  "dueDate": "2024-02-01"
}`

interface EndpointDef {
  value: string
  label: string
  method: 'GET' | 'POST'
  path: string
  description: string
  group: string
  paramMode: 'render' | 'raw'
  requiresWriteAccess?: boolean
  pathParamName?: string
  defaultBody?: string
  noBody?: boolean
}

const ENDPOINTS: EndpointDef[] = [
  // ── Client ──
  {
    value: 'client-render', label: 'Client Render', method: 'POST', path: '/client/render',
    description: 'Render by templateVersionId or templateId — returns file bytes',
    group: 'Client', paramMode: 'raw',
    defaultBody: JSON.stringify({ payloadJson: EXAMPLE_PAYLOAD, templateVersionId: EX_VERSION, outputFormat: 'pdf' }, null, 2),
  },
  {
    value: 'client-render-inline', label: 'Client Inline Render', method: 'POST', path: '/client/render/inline',
    description: 'Render ad-hoc DOCX (always watermarked)',
    group: 'Client', paramMode: 'raw',
    defaultBody: JSON.stringify({ docxBase64: '<base64-encoded docx>', payloadJson: EXAMPLE_PAYLOAD, outputFormat: 'pdf' }, null, 2),
  },
  {
    value: 'client-embed-token', label: 'Embed Token', method: 'POST', path: '/client/embed-token',
    description: 'Exchange API key for 1-hour embed JWT (render_share key)',
    group: 'Client', paramMode: 'raw', requiresWriteAccess: true,
    defaultBody: JSON.stringify({ templateId: null }, null, 2),
  },
  {
    value: 'client-templates', label: 'List Templates', method: 'POST', path: '/client/templates',
    description: "List templates accessible to this key's team",
    group: 'Client', paramMode: 'raw',
    defaultBody: JSON.stringify({ folderId: null }, null, 2),
  },
  {
    value: 'client-version-vars', label: 'Version Variables', method: 'POST', path: '/client/templates/version-variables',
    description: 'List variables in a template version — useful for building payloads',
    group: 'Client', paramMode: 'raw',
    defaultBody: JSON.stringify({ templateVersionId: EX_VERSION }, null, 2),
  },
  // ── Render ──
  {
    value: 'generate', label: 'Generate', method: 'POST', path: '/render/generate',
    description: 'Render template synchronously — returns file bytes',
    group: 'Render', paramMode: 'render',
  },
  {
    value: 'submit', label: 'Submit (async)', method: 'POST', path: '/render/submit',
    description: 'Queue render job — returns renderJobId',
    group: 'Render', paramMode: 'render',
  },
  {
    value: 'preview', label: 'Preview', method: 'POST', path: '/render/preview',
    description: 'Render preview (always PDF, ignores outputFormat)',
    group: 'Render', paramMode: 'render',
  },
  {
    value: 'validate', label: 'Validate', method: 'POST', path: '/render/validate',
    description: 'Pre-flight payload schema check — no document rendered',
    group: 'Render', paramMode: 'render',
  },
  {
    value: 'merge-generate', label: 'Merge & Generate', method: 'POST', path: '/render/merge-generate',
    description: 'Merge multiple payloads then render synchronously',
    group: 'Render', paramMode: 'raw',
    defaultBody: JSON.stringify({ templateVersionId: EX_VERSION, payloads: [EXAMPLE_PAYLOAD, '{"customerName":"Beta LLC","total":500}'], outputFormat: 'pdf', onMissing: 'blank' }, null, 2),
  },
  {
    value: 'warehouse', label: 'Warehouse Render', method: 'POST', path: '/render/warehouse',
    description: 'Query BigQuery / Snowflake / Redshift and render result',
    group: 'Render', paramMode: 'raw',
    defaultBody: JSON.stringify({ templateVersionId: EX_VERSION, provider: 'bigquery', query: 'SELECT * FROM dataset.table LIMIT 1', credentialsJson: {}, outputFormat: 'pdf', onMissing: 'blank', rowMode: 'first' }, null, 2),
  },
  {
    value: 'share', label: 'Create Share Link', method: 'POST', path: '/render/share',
    description: 'Create a shareable download link for a completed job (render_share key)',
    group: 'Render', paramMode: 'raw', requiresWriteAccess: true,
    defaultBody: JSON.stringify({ renderJobId: EX_JOB, ttlHours: 24, password: null, maxDownloads: null }, null, 2),
  },
  // ── Connectors ──
  {
    value: 'connector-pull', label: 'Connector Pull', method: 'POST', path: '/connectors/{connectorId}/pull',
    description: 'Pull record from Salesforce / HubSpot / Airtable / SharePoint and render (render_share key)',
    group: 'Connectors', paramMode: 'raw', requiresWriteAccess: true,
    pathParamName: 'connectorId',
    defaultBody: JSON.stringify({ recordId: 'abc123', objectType: null }, null, 2),
  },
  {
    value: 'ingestion', label: 'Fetch & Render', method: 'POST', path: '/ingestion/sources/{sourceId}/fetch-and-render',
    description: 'Fetch from Postgres / MySQL / MongoDB / REST source and render',
    group: 'Connectors', paramMode: 'raw',
    pathParamName: 'sourceId',
    defaultBody: JSON.stringify({ templateVersionId: EX_VERSION, query: 'SELECT * FROM orders WHERE id = $1', recordId: '123', rowMode: 'first', outputFormat: 'pdf', onMissing: 'blank' }, null, 2),
  },
]

const GROUPS = ['Client', 'Render', 'Connectors'] as const

const SELECT_OPTIONS = GROUPS.map(g => ({
  label: g,
  options: ENDPOINTS.filter(e => e.group === g).map(e => ({
    value: e.value,
    label: e.path.replace(/^\//, ''),
  })),
}))

interface ExecuteResult {
  status: number
  durationMs: number
  contentType: string
  isJson: boolean
  isBinary: boolean
  body: string
  byteSize?: number
  renderJobId?: string
}

export default function PlaygroundPage() {
  const isMobile = useIsMobile()
  const [apiKey,            setApiKey]            = useState('')
  const [savedKeys,         setSavedKeys]         = useState<ApiKey[]>([])
  // The API returns only a saved key's prefix (the secret is hashed), so picking one cannot fill the
  // field. It selects the key so the page can say which secret to paste and flag a mismatch.
  const [savedKeyId,        setSavedKeyId]        = useState('')
  const selectedSavedKey = savedKeys.find(k => k.apiKeyId === savedKeyId) ?? null
  const savedKeyMismatch = !!selectedSavedKey && !!apiKey.trim() && !apiKey.trim().startsWith(selectedSavedKey.keyPrefix)
  const [selectedValue,     setSelectedValue]     = useState('generate')
  // render-mode params
  const [templateVersionId, setTemplateVersionId] = useState('')
  const [payloadJson,       setPayloadJson]       = useState(EXAMPLE_PAYLOAD)
  const [outputFormat,      setOutputFormat]      = useState('pdf')
  const [onMissing,         setOnMissing]         = useState('blank')
  // raw-mode params
  const [bodyJson,          setBodyJson]          = useState('')
  const [pathParamValue,    setPathParamValue]    = useState('')
  // execution
  const [loading,           setLoading]           = useState(false)
  const [error,             setError]             = useState('')
  const [result,            setResult]            = useState<ExecuteResult | null>(null)
  const [trace,             setTrace]             = useState<RenderTrace | null>(null)
  const [traceLoading,      setTraceLoading]      = useState(false)

  useEffect(() => {
    apiKeysApiService.list().then(setSavedKeys).catch(() => null)
  }, [])

  useEffect(() => {
    const ep = ENDPOINTS.find(e => e.value === selectedValue)!
    if (ep.paramMode === 'raw') setBodyJson(ep.defaultBody ?? '')
    setPathParamValue('')
    setError('')
    setResult(null)
    setTrace(null)
  }, [selectedValue])

  const ep = ENDPOINTS.find(e => e.value === selectedValue)!

  async function handleExecute() {
    if (!apiKey.trim()) { setError('API key is required.'); return }

    let url = `${BASE_URL}${ep.path}`
    if (ep.pathParamName) {
      if (!pathParamValue.trim()) { setError(`${ep.pathParamName} is required.`); return }
      url = url.replace(`{${ep.pathParamName}}`, encodeURIComponent(pathParamValue.trim()))
    }

    const headers: Record<string, string> = { 'X-Api-Key': apiKey.trim() }
    let body: string | undefined

    if (ep.paramMode === 'render') {
      if (!templateVersionId.trim()) { setError('templateVersionId is required.'); return }
      let parsed: unknown
      try { parsed = JSON.parse(payloadJson) }
      catch { setError('Payload JSON is invalid.'); return }
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify({ templateVersionId: templateVersionId.trim(), payloadJson: JSON.stringify(parsed), outputFormat, onMissing })
    } else if (!ep.noBody && bodyJson.trim()) {
      headers['Content-Type'] = 'application/json'
      body = bodyJson
    }

    setLoading(true); setError(''); setResult(null); setTrace(null)
    const start = Date.now()
    try {
      const resp = await fetch(url, { method: ep.method, headers, body })
      const durationMs  = Date.now() - start
      const contentType = resp.headers.get('content-type') ?? ''
      const isJson   = contentType.includes('application/json')
      const isBinary = !isJson && !contentType.includes('text/')

      let responseBody: string
      let byteSize:    number | undefined
      let renderJobId: string | undefined

      if (isBinary) {
        const blob = await resp.blob()
        byteSize     = blob.size
        responseBody = `[Binary file: ${(blob.size / 1024).toFixed(1)} KB — ${contentType}]`
      } else {
        responseBody = await resp.text()
        if (isJson) {
          try {
            const p = JSON.parse(responseBody)
            responseBody = JSON.stringify(p, null, 2)
            renderJobId  = p.renderJobId ?? undefined
          } catch { /* keep raw */ }
        }
      }

      setResult({ status: resp.status, durationMs, contentType, isJson, isBinary, body: responseBody, byteSize, renderJobId })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed')
    } finally {
      setLoading(false)
    }
  }

  async function handleViewTrace(jobId: string) {
    setTraceLoading(true)
    try { setTrace(await renderApiService.getTrace(jobId)) }
    catch { /* ignore */ }
    finally { setTraceLoading(false) }
  }

  const statusColor = (s: number) =>
    s >= 200 && s < 300 ? colors.success : s >= 400 ? colors.error : colors.warning

  const rawRequest = (() => {
    const path = ep.pathParamName
      ? ep.path.replace(`{${ep.pathParamName}}`, pathParamValue.trim() || `{${ep.pathParamName}}`)
      : ep.path

    let body: string | undefined
    if (ep.paramMode === 'render') {
      let parsedOk = true
      try { JSON.parse(payloadJson) } catch { parsedOk = false }
      body = JSON.stringify({
        templateVersionId: templateVersionId.trim() || '<templateVersionId>',
        payloadJson: parsedOk ? payloadJson.trim() : '<invalid json>',
        outputFormat,
        onMissing,
      }, null, 2)
    } else if (!ep.noBody && bodyJson.trim()) {
      body = bodyJson.trim()
    }

    const lines: string[] = [
      `POST /api/v1${path} HTTP/1.1`,
      `Host: api.docmorphia.com`,
      `X-Api-Key: ${apiKey.trim() || '<api-key>'}`,
    ]
    if (body) lines.push('Content-Type: application/json')
    lines.push('')
    if (body) lines.push(body)
    return lines.join('\n')
  })()

  return (
    <div>
      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, margin: `0 0 ${spacing.xs}` }}>
        API Playground
      </h1>
      <p style={{ color: colors.textSecondary, fontSize: fontSize.base, margin: `0 0 ${spacing.xl}` }}>
        Try DocMorphia API endpoints interactively with your own API key.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: spacing.lg, alignItems: 'start' }}>
        {/* ── Request panel ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>

          {/* API Key */}
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, padding: spacing.lg }}>
            <label htmlFor="playground-api-key" style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, display: 'block', marginBottom: spacing.xs }}>
              API Key
            </label>
            {savedKeys.length > 0 && (
              <div style={{ marginBottom: spacing.xs }}>
                <ThemedSelect
                  placeholder="— select a saved key —"
                  value={savedKeyId || null}
                  onChange={v => {
                    const id = String(v)
                    setSavedKeyId(id)
                    const key = savedKeys.find(k => k.apiKeyId === id)
                    // Drop a value typed for a different key; keep one that already matches.
                    if (key && !apiKey.trim().startsWith(key.keyPrefix)) setApiKey('')
                  }}
                  options={savedKeys.filter(k => k.isActive !== false && !k.revokedAt).map(k => ({ value: k.apiKeyId, label: `${k.name} (${k.keyPrefix}…)` }))}
                />
              </div>
            )}
            <input
              id="playground-api-key"
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="live_…  or  test_…"
              style={{ width: '100%', padding: spacing.sm, border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, color: colors.text, fontFamily: fontFamily.mono, boxSizing: 'border-box' }}
            />
            <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: '4px' }}>
              Sent as <code style={{ fontFamily: fontFamily.mono }}>X-Api-Key</code> header. Never stored.
            </div>
            {selectedSavedKey && (
              <div
                role={savedKeyMismatch ? 'alert' : undefined}
                style={{ fontSize: fontSize.xs, marginTop: '4px', color: savedKeyMismatch ? colors.warning : colors.textSecondary }}
              >
                {savedKeyMismatch
                  ? `This key does not start with ${selectedSavedKey.keyPrefix}…, so it is not "${selectedSavedKey.name}".`
                  : `Paste the full secret for "${selectedSavedKey.name}" (starts with ${selectedSavedKey.keyPrefix}…). Only its prefix is stored, so it cannot be filled in for you.`}
              </div>
            )}
          </div>

          {/* Endpoint */}
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, padding: spacing.lg }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.sm }}>Endpoint</div>
            <Select
              options={SELECT_OPTIONS}
              value={SELECT_OPTIONS.flatMap(g => g.options).find(o => o.value === selectedValue) ?? null}
              onChange={opt => opt && setSelectedValue(opt.value)}
              menuPortalTarget={document.body}
              styles={{
                control:     s => ({ ...s, fontSize: fontSize.sm, borderColor: colors.border, boxShadow: 'none', '&:hover': { borderColor: colors.primary } }),
                option:      (s, { isFocused, isSelected }) => ({ ...s, fontSize: fontSize.sm, fontFamily: fontFamily.mono, background: isSelected ? colors.primary : isFocused ? colors.primaryLight : 'transparent', color: isSelected ? colors.onPrimary : colors.text }),
                groupHeading: s => ({ ...s, fontSize: fontSize.xs, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }),
                singleValue:  s => ({ ...s, fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.text }),
                menuPortal:   s => ({ ...s, zIndex: 9999 }),
              }}
            />
            <div style={{ fontFamily: fontFamily.mono, fontSize: fontSize.xs, marginTop: spacing.sm, wordBreak: 'break-all' }}>
              <span style={{ color: colors.success, fontWeight: 700 }}>POST</span>
              <span style={{ color: colors.textSecondary }}> /api/v1{ep.path}</span>
            </div>
            <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: '4px' }}>
              {ep.description}
              {ep.requiresWriteAccess && <span style={{ color: colors.warning }}> — requires render_share key</span>}
            </div>
          </div>

          {/* Parameters */}
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, padding: spacing.lg, display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text }}>Parameters</div>

            {/* Path param (for trace, verify, connector-pull, ingestion) */}
            {ep.pathParamName && (
              <>
                <label style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.textSecondary }}>
                  {ep.pathParamName} <span style={{ color: colors.error }}>*</span>
                </label>
                <input
                  value={pathParamValue}
                  onChange={e => setPathParamValue(e.target.value)}
                  placeholder={`Enter ${ep.pathParamName}`}
                  style={{ padding: spacing.sm, border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, color: colors.text, fontFamily: fontFamily.mono, boxSizing: 'border-box', width: '100%' }}
                />
              </>
            )}

            {/* Render-mode form */}
            {ep.paramMode === 'render' && (
              <>
                <label style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.textSecondary }}>
                  templateVersionId <span style={{ color: colors.error }}>*</span>
                </label>
                <input
                  value={templateVersionId}
                  onChange={e => setTemplateVersionId(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  style={{ padding: spacing.sm, border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, color: colors.text, fontFamily: fontFamily.mono, boxSizing: 'border-box', width: '100%' }}
                />

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: spacing.sm }}>
                  <div>
                    <label style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>outputFormat</label>
                    <ThemedSelect
                      value={outputFormat}
                      onChange={v => setOutputFormat(String(v))}
                      options={FORMATS.map(f => ({ value: f, label: f }))}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>onMissing</label>
                    <ThemedSelect
                      value={onMissing}
                      onChange={v => setOnMissing(String(v))}
                      options={ON_MISSING.map(m => ({ value: m, label: m }))}
                    />
                  </div>
                </div>

                <label style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.textSecondary }}>payloadJson</label>
                <textarea
                  value={payloadJson}
                  onChange={e => setPayloadJson(e.target.value)}
                  rows={10}
                  spellCheck={false}
                  style={{ padding: spacing.sm, border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.text, resize: 'vertical', boxSizing: 'border-box', width: '100%' }}
                />
              </>
            )}

            {/* Raw-mode JSON body */}
            {ep.paramMode === 'raw' && !ep.noBody && (
              <>
                <label style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.textSecondary }}>Request body (JSON)</label>
                <textarea
                  value={bodyJson}
                  onChange={e => setBodyJson(e.target.value)}
                  rows={10}
                  spellCheck={false}
                  style={{ padding: spacing.sm, border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.text, resize: 'vertical', boxSizing: 'border-box', width: '100%' }}
                />
              </>
            )}

            {ep.noBody && !ep.pathParamName && (
              <div style={{ fontSize: fontSize.xs, color: colors.textMuted }}>No request body — fill in the API key above and execute.</div>
            )}

            {error && <div style={{ color: colors.error, fontSize: fontSize.xs }}>{error}</div>}

            <button
              onClick={handleExecute}
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: loading ? colors.textMuted : colors.primary,
                color: colors.onPrimary, border: 'none', borderRadius: radius.sm,
                fontSize: fontSize.base, fontWeight: 700,
                cursor: loading ? 'default' : 'pointer',
                fontFamily: fontFamily.mono,
                wordBreak: 'break-all',
              }}
            >
              {loading ? 'Sending…' : `POST /api/v1${ep.path}`}
            </button>
          </div>
        </div>

        {/* ── Response panel ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          {/* Raw request */}
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, overflow: 'hidden' }}>
            <div style={{ padding: `${spacing.sm} ${spacing.md}`, background: colors.background, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'baseline', gap: spacing.sm }}>
              <span style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary }}>Raw Request</span>
              <span style={{ fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.textMuted }}>{BASE_URL}{ep.path}</span>
            </div>
            <pre style={{ margin: 0, padding: spacing.md, fontFamily: fontFamily.mono, fontSize: fontSize.xs, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#0A1413', color: '#e2e8f0', maxHeight: 320, overflowY: 'auto' }}>
              {rawRequest}
            </pre>
          </div>

          {!result && !loading && (
            <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, padding: spacing.xl, textAlign: 'center', color: colors.textMuted, fontSize: fontSize.sm }}>
              Execute a request to see the response here.
            </div>
          )}

          {loading && (
            <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, padding: spacing.xl, textAlign: 'center', color: colors.textSecondary, fontSize: fontSize.sm }}>
              Sending request…
            </div>
          )}

          {result && (
            <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, padding: `${spacing.sm} ${spacing.md}`, background: colors.background, borderBottom: `1px solid ${colors.border}` }}>
                <span style={{ fontSize: fontSize.sm, fontWeight: 700, color: statusColor(result.status), fontFamily: fontFamily.mono }}>
                  {result.status}
                </span>
                <span style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                  {result.durationMs} ms
                </span>
                {result.byteSize != null && (
                  <span style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                    {(result.byteSize / 1024).toFixed(1)} KB
                  </span>
                )}
                <span style={{ fontSize: fontSize.xs, color: colors.textMuted, marginLeft: 'auto', fontFamily: fontFamily.mono }}>
                  {result.contentType}
                </span>
              </div>
              <pre style={{ margin: 0, padding: spacing.md, fontFamily: fontFamily.mono, fontSize: fontSize.xs, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 420, overflowY: 'auto', background: '#0A1413', color: '#e2e8f0' }}>
                {result.body}
              </pre>
              {result.renderJobId && (
                <div style={{ padding: `${spacing.sm} ${spacing.md}`, borderTop: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                  <span style={{ fontSize: fontSize.xs, color: colors.textSecondary, fontFamily: fontFamily.mono }}>
                    Job: {result.renderJobId}
                  </span>
                  <button
                    onClick={() => handleViewTrace(result.renderJobId!)}
                    disabled={traceLoading}
                    style={{ padding: '3px 10px', background: 'none', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.xs, cursor: 'pointer', color: colors.text }}
                  >
                    {traceLoading ? 'Loading…' : 'View trace'}
                  </button>
                </div>
              )}
            </div>
          )}

          {trace && <TracePanel trace={trace} isMobile={isMobile} />}
        </div>
      </div>
    </div>
  )
}

function TracePanel({ trace, isMobile }: { trace: RenderTrace; isMobile: boolean }) {
  const statusColor = (s: string) =>
    s === 'succeeded' ? colors.success : s === 'failed' ? colors.error : s === 'running' ? colors.warning : colors.textSecondary

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, overflow: 'hidden' }}>
      <div style={{ padding: `${spacing.sm} ${spacing.md}`, background: colors.background, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, fontSize: fontSize.sm, color: colors.textSecondary }}>
        Render Trace
      </div>
      <div style={{ padding: spacing.md, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: spacing.sm }}>
        {[
          ['Status',        <span style={{ color: statusColor(trace.status), fontWeight: 600 }}>{trace.status}</span>],
          ['Duration',      trace.durationMs != null ? `${trace.durationMs} ms` : '—'],
          ['Pages',         trace.pageCount ?? '—'],
          ['Output size',   trace.outputByteSize != null ? `${(trace.outputByteSize / 1024).toFixed(1)} KB` : '—'],
          ['Created',       new Date(trace.createdAt).toLocaleTimeString()],
          ['Completed',     trace.completedAt ? new Date(trace.completedAt).toLocaleTimeString() : '—'],
          ['Has output',    trace.hasOutput ? 'Yes' : 'No'],
          ['Payload saved', trace.payloadAvailable ? 'Yes' : 'No'],
        ].map(([label, value]) => (
          <div key={String(label)}>
            <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: fontSize.sm, color: colors.text }}>{value}</div>
          </div>
        ))}
      </div>
      {trace.warnings.length > 0 && (
        <div style={{ padding: `0 ${spacing.md} ${spacing.md}` }}>
          <div style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.warning, marginBottom: spacing.xs }}>
            Warnings ({trace.warnings.length})
          </div>
          {trace.warnings.map((w, i) => (
            <div key={i} style={{ fontSize: fontSize.xs, color: colors.warning, padding: '2px 0', borderBottom: i < trace.warnings.length - 1 ? `1px solid ${colors.border}` : 'none' }}>
              {w}
            </div>
          ))}
        </div>
      )}
      {trace.errorMessage && (
        <div style={{ padding: `0 ${spacing.md} ${spacing.md}`, fontSize: fontSize.xs, color: colors.error }}>
          <span style={{ fontWeight: 600 }}>Error: </span>{trace.errorMessage}
        </div>
      )}
    </div>
  )
}
